<template>
	<div
		class="mobile-venue-info"
		:style="{
			top: `${headerHeight}px`,
			height: `calc(100dvh - ${headerHeight}px)`
		}"
	>
		<div>
			<google-map class="mobile-venue-info__map" />
			<venue-info-section v-if="address" :link="addressLink" target="_blank">
				<span class="section-text">
					<span class="bold">{{ formattedAddress.firstRow }}</span>
					<br />
					{{ formattedAddress.secondRow }}
				</span>
			</venue-info-section>
			<venue-info-section
				v-if="venuePhone"
				first-icon-name="phone-icon"
				:link="phoneLink"
			>
				<span class="bold section-text">
					{{ venuePhone }}
				</span>
			</venue-info-section>
			<venue-info-section
				v-if="venueEmail"
				first-icon-name="mail-icon"
				:link="mailLink"
			>
				<span class="bold section-text">
					{{ venueEmail }}
				</span>
			</venue-info-section>
			<section class="mobile-venue-info__schedule-section schedule">
				<clock-icon class="schedule-icon" />
				<Schedule need-highlight-active collapse-opened />
			</section>
		</div>
		<div>
			<domains-switcher
				class="switcher static mr-0 mt-auto mb-0 domains-switcher--pink"
				title="Events"
				sub-title="Tickets"
			/>
			<apps-banner />
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
// Components
import GoogleMap from '@/components/GoogleMap.vue'
import VenueInfoSection from '@/components/VenueInfo/VenueInfoSection.vue'
import Schedule from '@/components/Schedule.vue'
import ClockIcon from '@/assets/svg/ic_clock_solid_mobile_prev.svg'
import AppsBanner from '@/components/AppsBanner.vue'
// Helpers
import { getAddressLink } from '@/utils/helpers'
import DomainsSwitcher from '@/components/DomainsSwitcher.vue'

export default {
	name: 'MobileVenueInfo',

	props: {
		headerHeight: {
			type: Number,
			default: 0
		}
	},

	components: {
		DomainsSwitcher,
		AppsBanner,
		ClockIcon,
		Schedule,
		VenueInfoSection,
		GoogleMap
	},

	computed: {
		...mapGetters('venue', ['venueAddress', 'venuePhone', 'venueEmail']),

		address() {
			return this.venueAddress
		},

		addressLink() {
			return getAddressLink(this.address || {})
		},

		phoneLink() {
			return `tel:${this.venuePhone}`
		},

		mailLink() {
			return `mailto:${this.venueEmail}`
		},

		formattedAddress() {
			if (!this.address) return ''

			return this.address.addressLine2
				? {
						firstRow: `${this.address.addressLine1} ${this.address.addressLine2}`,
						secondRow: `${this.address.city}, ${this.address.region} ${this.address.postalCode}`
				  }
				: {
						firstRow: `${this.address.addressLine1}`,
						secondRow: `${this.address.city}, ${this.address.region} ${this.address.postalCode}`
				  }
		}
	}
}
</script>

<style scoped lang="scss">
@import './src/assets/styles/variables';

.mobile-venue-info {
	position: fixed;
	top: 60px;
	left: 0;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	width: 100%;
	padding: 24px 16px 0 16px;
	background-color: $midnight-navy;
	overflow: auto;
	z-index: 105;

	.domains-switcher {
		display: flex;
		align-items: center;
		justify-content: center;
		margin: 4px 0 24px 0;
	}

	.banner-bottom {
		position: relative;
		width: 100%;
		padding: 8px 24px;
		background: $spectre-violet;
		border: 1px solid rgba($white, 0.2);
		border-bottom: none;
		border-top-right-radius: 12px;
		border-top-left-radius: 12px;

		/deep/ .store-button {
			margin-bottom: 0;
			margin-top: 0;
		}

		/deep/ .banner-title {
			padding-top: 0;
			font-weight: 700;
		}

		/deep/ .btn-close {
			display: none;
		}
	}

	&__map {
		width: 100%;
		height: 206px;
	}

	&__schedule-section {
		position: relative;
		padding-top: 16px;

		.schedule-icon {
			position: absolute;
			left: 8px;
			top: 22px;
			color: $soft-lavender;
		}
		/deep/ .collapse__header {
			padding: 12px 49px 12px 56px;

			.schedule-title {
				margin-bottom: 0;
				font-size: 20px;
				line-height: 24px;
			}

			&::before {
				border-color: $grand-blue;
				right: 16px;
			}
		}

		/deep/ .schedule-list {
			max-width: none;
			width: 100%;
			padding: 0;

			.schedule-list-item {
				border-color: $royal-purple;
			}
		}
	}

	.section-text {
		max-width: calc(100% - 112px);
		overflow: hidden;
	}
}
</style>
